import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BottomNav from "./BottomNav";
import BackgroundDecor from "./BackgroundDecor";
import StorageLockScreen from "./StorageLockScreen";
import CaptureOrchestrator from "./CaptureOrchestrator";
import { CaptureProvider, useCaptureContext, type CaptureType } from "@/contexts/CaptureContext";
import type { CardContext } from "@/types/cardContext";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { useChildren } from "@/hooks/useChildren";
import { useMemories } from "@/hooks/useMemories";
import { useTheme, type ThemeId } from "@/hooks/useTheme";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useAuth } from "@/hooks/useAuth";
import { useSpaces } from "@/hooks/useSpaces";
import { useActiveSpace } from "@/hooks/useActiveSpace";
import SpaceSwitcher from "./SpaceSwitcher";
import CreateSpaceSheet from "./CreateSpaceSheet";
import { switchSpace } from "@/lib/spaceSwitch";
import { useAcornBalance } from "@/contexts/AcornContext";
import { useConnectivity } from "@/hooks/useConnectivity";
import { OfflineBanner } from "@/components/OfflineBanner";
import { OfflineScreen } from "@/components/OfflineScreen";
import { cancelDowngrade } from "@/lib/subscriptionHelpers";
import { useStorageMemoryCount } from "@/hooks/queries/useStorageMemoryCount";
import { prefetchFeedEngineData } from "@/hooks/queries/useFeedEngineData";
import { toast } from "sonner";
import { fireEvent } from "@/lib/events";

const THEME_BG: Record<ThemeId, string> = {
  acorns:   "#FEF3E2",
  campfire: "#FDF6EF",
  forest:   "#ECF4EC",
  night:    "#131B2E",
  garden:   "#F0F7ED",
  magical:  "#FDF0F8",
  ocean:    "#EDF5FD",
  stars:    "#F0F0F8",
};

const TIER_LABELS: Record<string, string> = {
  seedling:    "Starter",
  ember:       "Extra Storage",
  keeper:      "Plus",
  storyteller: "Premium",
};

// ── Inner shell (needs CaptureProvider to be above it) ───────────────────────

function MobileShellInner() {
  const { t } = useTranslation();
  const [createSpaceOpen, setCreateSpaceOpen] = useState(false);
  const [cancellingDowngrade, setCancellingDowngrade] = useState(false);
  const { step: captureStep, startCapture } = useCaptureContext();
  const isProcessing = captureStep === "processing";
  const { children, activeChild } = useChildren();
  const { memories: allMemories, isLoading: memoriesLoading, error: memoriesError, removeMemory, refresh: refreshMemoriesHook } = useMemories();
  const { theme } = useTheme();
  const { tier, subscription, isLoading: entitlementsLoading, scheduledTier, periodEnd, refresh, isStorageFull, storageUsedMb, storageLimitMb, canRecord, storageUsedPercent } = useEntitlements();

  // Past-due hard gate: paid user whose latest invoice failed. Route guard
  // redirects every in-shell page to /account-locked until they either
  // update their card (Stripe portal → webhook flips status back to 'active')
  // or self-downgrade to Seedling. Seedling users can't be past_due, but we
  // exclude the tier anyway so the gate can never lock a free user out.
  const pastDueGate =
    !entitlementsLoading && subscription?.status === "past_due" && tier !== "seedling";

  // Connectivity: hasAnyData = true once memories have finished loading (even with
  // 0 results or a query error). Only false during the initial load before the
  // first fetch completes — this prevents offline screen flashing on startup.
  const hasAnyData = allMemories.length > 0 || !memoriesLoading;
  const { showBanner, showFullscreen } = useConnectivity(hasAnyData);
  const { user, activeSpaceRole } = useAuth();
  const { count: memoryCount } = useStorageMemoryCount(user?.id, isStorageFull);
  const { spaces } = useSpaces();
  const { space: activeSpace } = useActiveSpace();
  const { balance: acornBalance } = useAcornBalance();
  const navigate = useNavigate();
  const location = useLocation();

  const handleCancelDowngrade = async () => {
    if (!user || cancellingDowngrade) return;
    setCancellingDowngrade(true);
    try {
      await cancelDowngrade(user.id);
      refresh();
      toast.success(t("toast.downgradeCancelled", { tier: TIER_LABELS[tier] }));
    } catch (err) {
      console.error(err);
      toast.error(t("toast.downgradeCancelError"));
    } finally {
      setCancellingDowngrade(false);
    }
  };

  // ── Pre-warm the home-feed engine's input cache once per user-id ────────────
  // Eliminates the skeleton flash on the first Home visit of a session by
  // populating TanStack's cache before HomeFeed mounts. Idempotent — fresh
  // cached entries are skipped by prefetchQuery.
  const prewarmedForUserRef = useRef<string | null>(null);
  useEffect(() => {
    const uid = user?.id;
    if (!uid) return;
    if (prewarmedForUserRef.current === uid) return;
    prewarmedForUserRef.current = uid;
    void prefetchFeedEngineData(uid);
  }, [user?.id]);

  // ── storage.threshold.reached — fires only when crossing 80% or 100% ────────
  // prevStoragePercentRef starts null so the first load never fires (no crossing).
  const prevStoragePercentRef = useRef<number | null>(null);
  useEffect(() => {
    if (!user || entitlementsLoading) return;
    const prev = prevStoragePercentRef.current;
    prevStoragePercentRef.current = storageUsedPercent;
    if (prev === null) return; // first load — establish baseline, don't fire
    if (storageUsedPercent >= 100 && prev < 100) {
      fireEvent("storage.threshold.reached", {
        userId: user.id,
        percentUsed: 100,
        threshold: 100,
      });
    } else if (storageUsedPercent >= 80 && prev < 80) {
      fireEvent("storage.threshold.reached", {
        userId: user.id,
        percentUsed: storageUsedPercent,
        threshold: 80,
      });
    }
  }, [storageUsedPercent, user, entitlementsLoading]);

  const showUpgradePill =
    !entitlementsLoading &&
    tier === "seedling" &&
    activeSpaceRole === "owner" &&
    location.pathname !== "/upgrade";

  const handleOpenCapture = (opts?: {
    contextPrompt?: string;
    captureType?: "voice" | "drawing" | "photo" | "written" | "video";
    cardContext?: CardContext;
    voiceRequired?: boolean;
    collectionId?: string;
    collectionIcon?: string;
    collectionColor?: string;
    collectionParticipantConnectionIds?: string[];
    autoStart?: boolean;
  }) => {
    if (!canRecord) {
      toast.error(t("toast.storageFull"));
      return;
    }
    // Map legacy card capture_type values → current CaptureType.
    // Content library rows still use "voice"/"written"/"video"; rename happened in 98883ba.
    const typeMap: Record<string, CaptureType> = {
      voice: "voice_moment",
      written: "general_memory",
      video: "shortie",
    };
    const type = opts?.captureType
      ? (typeMap[opts.captureType] ?? opts.captureType) as CaptureType
      : undefined;

    startCapture({
      type,
      autoStart: opts?.autoStart,
      cardContext: opts?.cardContext,
      contextPrompt: opts?.contextPrompt,
      voiceRequired: opts?.voiceRequired,
      collectionId: opts?.collectionId,
      collectionIcon: opts?.collectionIcon,
      collectionColor: opts?.collectionColor,
      collectionParticipantConnectionIds: opts?.collectionParticipantConnectionIds,
    });
  };

  if (pastDueGate) {
    return <Navigate to="/account-locked" replace />;
  }

  const CHILD_BLOCKED_PATHS = new Set([
    "/tribe", "/collections", "/themes", "/stash", "/upgrade", "/welcome-gift",
  ]);
  if (activeSpaceRole === "child" && CHILD_BLOCKED_PATHS.has(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  // Contributors can record + browse the feed + their profile + see they're in
  // a family, but everything owner-facing (collections, upgrade/paywall, stash
  // rewards, gift welcome) is out of scope for their role.
  const CONTRIBUTOR_BLOCKED_PATHS = new Set([
    "/collections", "/themes", "/upgrade", "/stash", "/welcome-gift",
  ]);
  if (activeSpaceRole === "contributor" && CONTRIBUTOR_BLOCKED_PATHS.has(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="h-[100dvh] bg-muted/50 flex justify-center overflow-hidden">
      <div
        className="w-full max-w-[430px] relative flex flex-col transition-colors duration-500"
        style={{
          backgroundColor: THEME_BG[theme] ?? THEME_BG.campfire,
          height: "100dvh",
        }}
      >
        <BackgroundDecor theme={theme} />

        {/* Storage full overlay */}
        {isStorageFull && !(() => {
          try {
            const until = localStorage.getItem("campfire_storage_reprieve_until");
            return until && new Date(until) > new Date();
          } catch { return false; }
        })() && (
          <StorageLockScreen
            storageUsedMb={storageUsedMb}
            storageLimitMb={storageLimitMb}
            memoryCount={memoryCount}
          />
        )}

        {/* Top-left floating controls — space switcher */}
        {!isProcessing && (
        <div
          className="absolute left-4 z-40 flex items-center gap-2"
          style={{ top: "calc(env(safe-area-inset-top) + 1rem)" }}
        >
          <SpaceSwitcher
            spaces={spaces}
            activeSpaceId={activeSpace?.id ?? null}
            onSwitch={async (spaceId) => {
              if (!user) return;
              const target = spaces.find((s) => s.spaceId === spaceId);
              const ok = await switchSpace(user.id, spaceId);
              if (!ok) {
                toast.error(t("spaces.switcher.switchError"));
                return;
              }
              toast.success(
                t("spaces.switcher.switchSuccess", {
                  name: target?.spaceName ?? "",
                }),
              );
              navigate("/", { replace: true });
            }}
            onCreate={() => setCreateSpaceOpen(true)}
          />
        </div>
        )}

        {/* Top-right floating controls */}
        {!isProcessing && (
        <div
          className="absolute right-4 z-40 flex items-center gap-2"
          style={{ top: "calc(env(safe-area-inset-top) + 1rem)" }}
        >
          {showUpgradePill && (
            <button
              onClick={() => navigate("/upgrade")}
              className="font-medium transition-transform active:scale-95 hover:scale-[1.03]"
              style={{
                backgroundColor: "#EEF3EE", color: "#2E4D30",
                fontSize: "14px", borderRadius: "14px", padding: "7px 14px",
              }}
            >
              Upgrade 🔥
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate("/stash")}
            className="flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-transform active:scale-95"
            style={{ backgroundColor: "#FDF0EE", color: "#C45B4B" }}
          >
            <span>🌰</span>
            <span>{acornBalance}</span>
          </button>
        </div>
        )}

        {/* Scheduled downgrade banner */}
        {scheduledTier && periodEnd && (
          <div
            className="relative z-20 flex items-center justify-between gap-2"
            style={{ backgroundColor: "#EFF6FF", color: "#1D4ED8", fontSize: 13, padding: "10px 16px" }}
          >
            <span>
              Your plan changes to{" "}
              <strong>{TIER_LABELS[scheduledTier] ?? scheduledTier}</strong> on{" "}
              {periodEnd.toLocaleDateString(undefined, { month: "short", day: "numeric" })}.
              You keep {TIER_LABELS[tier]} features until then.
            </span>
            <button
              type="button"
              onClick={handleCancelDowngrade}
              disabled={cancellingDowngrade}
              style={{
                fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
                opacity: cancellingDowngrade ? 0.5 : 1,
                background: "none", border: "none", color: "#1D4ED8",
                cursor: "pointer", textDecoration: "underline", padding: 0,
              }}
            >
              {cancellingDowngrade ? "Cancelling…" : "Cancel"}
            </button>
          </div>
        )}

        {/* Offline banner — non-blocking, auto-hides when connectivity restored */}
        <OfflineBanner visible={showBanner} />

        <div
          id="capture-overlay-host"
          className={`flex-1 relative z-10 ${isProcessing ? "overflow-hidden" : "overflow-y-auto"}`}
        >
          {showFullscreen ? (
            <OfflineScreen />
          ) : (
            <Outlet context={{
              openRecording: handleOpenCapture,
              memories: allMemories,
              memoriesLoading,
              memoriesError,
              removeMemory,
              refreshMemories: refreshMemoriesHook,
            }} />
          )}
        </div>

        {!isProcessing && (
          <BottomNav onRecord={(opts) => handleOpenCapture(opts)} activeSpaceRole={activeSpaceRole} />
        )}

        {/* Unified capture pipeline */}
        <CaptureOrchestrator />

        <CreateSpaceSheet open={createSpaceOpen} onOpenChange={setCreateSpaceOpen} />

        <Sonner
          position="top-center"
          offset={56}
          visibleToasts={2}
          duration={3000}
          style={{ zIndex: 40 }}
        />
      </div>
    </div>
  );
}

// ── Wrapper with CaptureProvider ─────────────────────────────────────────────

const MobileShell = () => (
  <CaptureProvider>
    <MobileShellInner />
  </CaptureProvider>
);

export default MobileShell;
